import React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import { Link, RouteComponentProps, withRouter, generatePath } from 'react-router-dom';
import * as InviteSupplierStateController from '../../store/StateControllers/Supplier/InviteSuppliersStateController';
import { ToxnotReactStatusComponent, ToxnotReactStatusComponentState } from '../core/ToxnotReactStatusComponent';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';
import { ReportTypesManager } from '../../store/Managers/ReportTypesManager';
import { CollapsibleContainer } from '../core/CollapsibleContainer';
import { StandardRequestedDocumentType, SurveyComplianceRegulationValues, SurveyTemplateDataModel, SustainabilityAndCircularityIds, VocIds } from '../../store/Models/Surveys/SurveyDataModel';
import SelectSuppliersMaterialsTable from '../core/SelectSuppliersMaterialsTable';
import { surveyableColumns } from '../../store/StateControllers/Survey/SurveyStateController';
import * as SelectSuppliersMaterialsTableStateController from '../../store/StateControllers/Supplier/SelectSuppliersMaterialsTableStateController';
import './styles.css';
import { CreateAndEditSurveyComponentPaths } from '../Survey/SurveyComponentPaths';
import UserAddNamePromptModal from '../Survey/UserAddNamePromptModal';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { TeamRole } from '../../store/Models/UserContext';

// ===============================================================================

interface StatusMessageBundle {
    state: ToxnotReactStatusComponentState;
    localizedMessage?: string | JSX.Element;
    localizedHeader?: string;
}

// ===============================================================================

interface RequestedDataRenderType {
    type: InviteSupplierStateController.RequestedDataSectionType;
    localizedFriendlyDescription: string;
    specifics: string[];
}

// ===============================================================================

interface OwnProps {
    shouldMakeTnSection: boolean
}

// ===============================================================================

export type InviteSuppliersProps =
    OwnProps &
    RouteComponentProps &
    ToxnotComponentProperties &
    InviteSupplierStateController.InviteSuppliersState &
    typeof InviteSupplierStateController.actionCreators & // ... plus action creators we've requested
    SelectSuppliersMaterialsTableStateController.SelectSuppliersMaterialsTableState &
    typeof SelectSuppliersMaterialsTableStateController.actionCreators;

// ===============================================================================

interface InviteSuppiersState {
    openTemplateSelector: boolean;
    openedRequestDataSections: InviteSupplierStateController.RequestedDataSectionType[];
}

// ===============================================================================

//
// Renders the main component for Inviting Suppliers.
//

class InviteSuppliersCore extends ToxnotSignInRequiredComponent<InviteSuppliersProps, InviteSuppiersState> {

    // ===============================================================================

    private _inviteSuppliersStatusToStatusState: Record<InviteSupplierStateController.InvitingSuppliersStatus, StatusMessageBundle> = {
        [InviteSupplierStateController.InvitingSuppliersStatus.Normal]: { state: ToxnotReactStatusComponentState.Normal },
        [InviteSupplierStateController.InvitingSuppliersStatus.StartingInvitation]: { state: ToxnotReactStatusComponentState.BeginOperation, localizedMessage: this.localizeString("Inviting Suppliers..."), localizedHeader: this.localizeString("Invite Suppliers") },
        [InviteSupplierStateController.InvitingSuppliersStatus.InvitationSent]: { state: ToxnotReactStatusComponentState.OperationSucceeded, localizedMessage: <><p>{this.localizeString("Your requests have been sent!")}</p><p>{this.localizeString("We'll email you when your requests are accepted.")}</p><p>{this.localizeString("Go to")} <a href={"/Surveys/SupplierSurveys"}>{this.localizeString("My Surveys")}</a> {this.localizeString("to check the status of your requests.")}</p></>, localizedHeader: this.localizeString("Invite Suppliers") },
        [InviteSupplierStateController.InvitingSuppliersStatus.InviteFailed]: { state: ToxnotReactStatusComponentState.OperationFailed, localizedMessage: this.localizeString("Sorry, there was an issue accessing the database."), localizedHeader: this.localizeString("Invite Suppliers") },
        [InviteSupplierStateController.InvitingSuppliersStatus.InviteFailedInvalidEmailToken]: { state: ToxnotReactStatusComponentState.OperationFailed, localizedMessage: this.localizeString("Your email connection has expired. Please reconnect your account."), localizedHeader: this.localizeString("Invite Suppliers") }
    };

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }
    // ===============================================================================

    constructor(props: InviteSuppliersProps) {
        super(props);

        this.state = {
            openTemplateSelector: false,
            openedRequestDataSections: []
        };
    }

    // ===============================================================================

    public componentDidMount() {
        this.props.initialize();
    }

    // ===============================================================================

    public componentDidUpdate(prevProps: InviteSuppliersProps) {
        if (prevProps.isSelectingSuppliersLoading && !this.props.isSelectingSuppliersLoading) {
            this.scrollToBottom();
        }
    }

    // ===============================================================================

    public componentWillUnmount() {
        this.props.resetState(false);
    }

    // ===============================================================================

    private renderLoadingElement = () => (
        <div className="text-center">
            <LoadingSpinner size={LoadingSpinnerSize.Medium} />
        </div>
    );

    // ===============================================================================

    private renderStatusComponent = () => {
        const statusData = this._inviteSuppliersStatusToStatusState[this.props.invitingSuppliersStatus];

        return (
            <>
                <ToxnotReactStatusComponent
                    appServices={this.props.appServices}
                    state={statusData.state}
                    localizedOperationMessage={statusData.localizedMessage}
                />
                {statusData.state === ToxnotReactStatusComponentState.OperationSucceeded &&
                    <div>
                        <button type="button" className="btn btn-toxnot-primary" onClick={e => this.inviteSuppliersDone_click()}>{this.localizeString("Done")}</button>
                    </div>
                }
            </>
        );
    }

    // ===============================================================================

    private renderExceededQuotaWarning = () => (
        <>
            <div className="alert alert-warning">
                <p>
                    {this.localizeString("Sending these requests will cause you to go over how many materials you are allowed in your library. Therefore, some surveys may be sent as \"open requests\" allowing your suppliers to choose the materials they want to respond with.")}
                </p>
                <p>
                    {this.localizeString("You can upgrade your account to be allowed more materials.")} <a href="/Account/Upgrade">{this.localizeString("Click here")}</a> {this.localizeString("to view upgraded plans.")}
                </p>
            </div>
            <button type="button" id="dismiss-send-customized-surveys" className="btn btn-default" onClick={e => this.props.updateActiveView(InviteSupplierStateController.InviteSuppliersViewType.Main)}>{this.localizeString("Cancel")}</button>
            {" "}
            <button type="button" id="submit-send-customized-surveys" className="btn btn-toxnot-primary" onClick={e => this.props.inviteSuppliers(InviteSupplierStateController.CreateSurveysMode.Send, true)}>{this.localizeString("Continue Anyway")}</button>
        </>
    );

    // ===============================================================================

    private renderLeftSideSection = () => (
        <div id="invite-suppliers-core-left-side-section">
            <div id="left-side-section-header">
                {this.localizeString("Passport Data Requested")}
            </div>
            <label className="note with-padding">
                {this.localizeString("Change the data you're requesting:")}
            </label>
            <div id="selected-template-name">
                { this.renderTemplatePicker() }
            </div>
            <label className="note">
                {this.localizeString("Template Settings:")}
            </label>
            {this.renderRequestedDataOptions()}
        </div>
    );

    // ===============================================================================

    private renderTemplatePicker() {
        const sortedTemplatesArray = this.props.templates.orderBy(t => t.name);

        return (
            <div className="btn-group">
                <button type="button" id="template-dropdown" className="btn btn-default btn-sm dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"> <span className="template-dropdown-button-text">{(InviteSupplierStateController.getCurrentlySelectedTemplateFromState(this.props)?.name ?? "No Template")} </span> <span className="caret"></span></button>
                <ul className="dropdown-menu scrollable">
                    {sortedTemplatesArray.map((data, index) => {
                        return (
                            <li key={index}>
                                <a role="button" onClick={e => this.templatePicker_templateSelected(data.templateId!)}>
                                    <div className="title">{data.name}</div>
                                </a>
                            </li>
                        );
                    })}
                    <li key={sortedTemplatesArray.length} role="separator" className="divider"></li>
                    <li key={sortedTemplatesArray.length + 1}>
                        <Link role="button" to={{
                            pathname: generatePath(CreateAndEditSurveyComponentPaths.NewSurveyPath, { startFromSurveyTemplate: this.props.currentlySelectedTemplateId ?? "", pageIndexToStartOn: "1" }),
                            state: {
                                supplierRowModels: InviteSupplierStateController.removeRowIdentifiersFromSuppliersToInvite(this.props.supplierRowModels)
                            },
                        }}>
                            <div className="title">{this.localizeString("Edit Template...")}</div>
                        </Link>
                    </li>
                </ul>
            </div>
        );
    }

    // ===============================================================================

    private getFriendlyNameForSpecific(specific: string) {
        const map: { [type: string]: string } = {
            [SustainabilityAndCircularityIds.EmbodiedCarbon.toString()]: this.localizeString("Embodied Carbon"),
            [SustainabilityAndCircularityIds.WaterUsageData.toString()]: this.localizeString("Water Usage Data"),
            [SustainabilityAndCircularityIds.EndOfLifeOptions.toString()]: this.localizeString("End of Life Options"),
            [SustainabilityAndCircularityIds.Contact.toString()]: this.localizeString("Contact"),
            [SustainabilityAndCircularityIds.Instructions.toString()]: this.localizeString("Instructions"),
            [SustainabilityAndCircularityIds.CarbonTransparency.toString()]: this.localizeString("Carbon Transparency"),
            [SustainabilityAndCircularityIds.CarbonOther.toString()]: this.localizeString("Carbon Assessment, Commitments, and Optimization"),
            [VocIds.VocTransparency.toString()]: this.localizeString("VOC Transparency"),
            [VocIds.VocOther.toString()]: this.localizeString("VOC Assessment, Commitments, and Optimization"),
            ...this.props.columnIdsToFriendlyNames,
            ...SurveyComplianceRegulationValues.getLocalizedDisplayNameMap(this.props.dynamiceRegulations)
        };

        // This is a reminder specifc and we treat these slightly different...
        if (specific.includes("tn-reminder-")) {
            return specific.substring(("tn-reminder-").length) + " " + this.localizeString("days after request")
        }

        return map[specific] ?? specific;
    }

    // ===============================================================================

    private renderRequestedDataSpecificsForType = (requestedDataType: InviteSupplierStateController.RequestedDataSectionType, specifics: string[]) => {
        let body: JSX.Element;

        if (specifics.length === 0) {
            body = (
                <p>{this.localizeString("View Details in Editor.")}</p>
            );
        }
        else {
            const includedSpecifics = specifics.filter(s => this.props.requestedDataSections[requestedDataType]?.includes(s) ?? false);
            const excludedSpecifics = specifics.filter(s => !this.props.requestedDataSections[requestedDataType]?.includes(s) ?? true);

            body = (
                <>
                    {includedSpecifics.length > 0 &&
                        <div className="requested-data-type-specifics-included">
                            {includedSpecifics.map((s, si) => (<div key={si}>{this.getFriendlyNameForSpecific(s)}</div>))}
                        </div>
                    }
                    {excludedSpecifics.length > 0 &&
                        <div className={"requested-data-type-specifics-excluded" + " " + (includedSpecifics.length > 0 ? "border" : "")}>
                            {includedSpecifics.length > 0 && <p>{this.localizeString("Not selected:")}</p>}
                            {excludedSpecifics.map((s, si) => (<div key={si}>{this.getFriendlyNameForSpecific(s)}</div>))}
                        </div>
                    }
                </>
            );
        }

        return (
            <div className="requested-data-type-specifics-container">
                {body}
            </div>
        );
    }

    // ===============================================================================

    /**
     * This method returns all possible requested data sections and the all the specifics for each
     * of those sections. This is for comparing with what the current template is asking for,
     * so a user can see what sections and specifics are included and excluded. 
     * 
     * For some requested data sections i.e reminders, it doesn't make sense to list out all possible
     * specific values (since that would be every day until infinity). Therefore, with these
     * sections we simply list out what the template is asking for. If the template is not asking this section
     * it no specifics are rendered.
     */
    private getAllRequestedDataToAskFor() {
        const template = InviteSupplierStateController.getCurrentlySelectedTemplateFromState(this.props);

        //
        // Docs and Certs
        //

        const otherDocsAndCertsToAdd = template?.dataRequested.documentsCertificationList.map(d => d.documentNameIfOther).filter(d => d.trim() !== "") ?? [];
        const allDocsAndCerts = Object.keys(StandardRequestedDocumentType).concat(otherDocsAndCertsToAdd);

        //
        // Material Disclosure
        // 

        const materialDisclosureSpecifics = surveyableColumns.map(c => c.columnId);

        //
        // Compliance
        //

        const complianceSpecifics = this.props.dynamiceRegulations.orderBy(dr => dr.config.name).map(dr => dr.regulationId);

        //
        // Circularity
        //

        const circularitySpecifics = [SustainabilityAndCircularityIds.EndOfLifeOptions, SustainabilityAndCircularityIds.Contact, SustainabilityAndCircularityIds.Instructions]

        const ret: RequestedDataRenderType[] = [
            { type: InviteSupplierStateController.RequestedDataSectionType.MaterialDisclosure, localizedFriendlyDescription: this.localizeString("Material Disclosure"), specifics: materialDisclosureSpecifics },
            { type: InviteSupplierStateController.RequestedDataSectionType.Compliance, localizedFriendlyDescription: this.localizeString("Compliance"), specifics: complianceSpecifics },
            { type: InviteSupplierStateController.RequestedDataSectionType.Circularity, localizedFriendlyDescription: this.localizeString("Circularity"), specifics: circularitySpecifics },
            { type: InviteSupplierStateController.RequestedDataSectionType.Sustainability, localizedFriendlyDescription: this.localizeString("Lifecycle Impacts"), specifics: [SustainabilityAndCircularityIds.EmbodiedCarbon, SustainabilityAndCircularityIds.WaterUsageData, SustainabilityAndCircularityIds.CarbonTransparency, SustainabilityAndCircularityIds.CarbonOther] },
            { type: InviteSupplierStateController.RequestedDataSectionType.Voc, localizedFriendlyDescription: this.localizeString("VOC"), specifics: [VocIds.VocTransparency, VocIds.VocOther] },
            { type: InviteSupplierStateController.RequestedDataSectionType.DocsAndCerts, localizedFriendlyDescription: this.localizeString("Documents & Certifications"), specifics: allDocsAndCerts },
            { type: InviteSupplierStateController.RequestedDataSectionType.CompletionInstructions, localizedFriendlyDescription: this.localizeString("Completion Instructions"), specifics: this.getSpecificsFromRequestingData(InviteSupplierStateController.RequestedDataSectionType.CompletionInstructions) },
            { type: InviteSupplierStateController.RequestedDataSectionType.RequestInstructions, localizedFriendlyDescription: this.localizeString("Request Instructions"), specifics: this.getSpecificsFromRequestingData(InviteSupplierStateController.RequestedDataSectionType.RequestInstructions) },
            { type: InviteSupplierStateController.RequestedDataSectionType.Reminders, localizedFriendlyDescription: this.localizeString("Scheduled Reminders"), specifics: this.getSpecificsFromRequestingData(InviteSupplierStateController.RequestedDataSectionType.Reminders) },
            { type: InviteSupplierStateController.RequestedDataSectionType.CustomQuestionnaires, localizedFriendlyDescription: this.localizeString("Custom Questionnaires"), specifics: this.getSpecificsFromRequestingData(InviteSupplierStateController.RequestedDataSectionType.CustomQuestionnaires) }
        ];

        return ret;
    }

    // ===============================================================================
    
    /**
     * Given a requested data type, this will return whatever is in the current requesting
     * data specifics in the state for that type. This is useful, when the specifics we
     * are displaying are just whatever the user is already requesting.
     * 
     * @param type the tpe of Requested Data we want to check in the state.
     */
    private getSpecificsFromRequestingData(type: InviteSupplierStateController.RequestedDataSectionType): string[] {
        if (!this.props.requestedDataSections[type]) {
            return [];
        }

        return this.props.requestedDataSections[type];
    }

    // ===============================================================================

    private renderRequestedDataOptions() {
        const requestedSectionList = this.getAllRequestedDataToAskFor()
            .sort((a, b) => {
                const a_section = this.props.requestedDataSections[a.type];
                const b_section = this.props.requestedDataSections[b.type];

                if (a_section && !b_section) {
                    return -1;
                }
                else if (b_section && !a_section) {
                    return 1;
                }
                else {
                    if (a.type === InviteSupplierStateController.RequestedDataSectionType.Languages) {
                        return 1;
                    }
                    else if (b.type === InviteSupplierStateController.RequestedDataSectionType.Languages) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                }
            });

        return (
            <div id="requested-data-types-container">
                {requestedSectionList.map((d, i) => (
                    <CollapsibleContainer
                        key={i}
                        opened={this.state.openedRequestDataSections.includes(d.type)}
                        onClick={() => this.dataSectionToggle_click(d.type)}
                        title={d.localizedFriendlyDescription}
                        className={(this.props.requestedDataSections[d.type] !== undefined) ? "requested" : "not-requested"}
                        hideToggle={d.specifics.length === 0}
                        >
                        {this.state.openedRequestDataSections.includes(d.type) && this.renderRequestedDataSpecificsForType(d.type, d.specifics)}
                    </CollapsibleContainer>
                    ))}
            </div>
        );
    }

    // ===============================================================================

    public renderConcreteComponent() {

        let fullContent: JSX.Element;
        const hasLinkedMaterials = this.suppliersToInviteHaveInventoriesWithLinkedMaterials();
        const hasMaterialDisclosureLinked = this.props.currentlySelectedTemplateId !== null
            ? (InviteSupplierStateController.getCurrentlySelectedTemplateFromState(this.props)?.dataRequested.materialDisclosureAsked ?? false)
            : false;
        
        if (this.props.isLoading) {
            fullContent = this.renderLoadingElement();
        }
        else {
            const supplierOnlyMode = InviteSupplierStateController.isSupplierOnlyMode(this.props);

            switch (this.props.activeView) {
                case InviteSupplierStateController.InviteSuppliersViewType.InvitingSuppliers:
                    fullContent = this.renderStatusComponent();
                    break;
                case InviteSupplierStateController.InviteSuppliersViewType.ExceedQuotaWarning:
                    fullContent = this.renderExceededQuotaWarning();
                    break;
                default:
                    fullContent = (
                        <React.Fragment>
                            <div id="invite-suppliers-action-buttons">
                                <button type="button" id="auto-send-requests-btn" className="btn btn-toxnot-primary" disabled={hasLinkedMaterials && hasMaterialDisclosureLinked} onClick={e => this.sendRequestsButton_click(InviteSupplierStateController.CreateSurveysMode.Send)}>{this.localizeString("Send Requests")}</button>
                                {" "}
                                <button type="button" id="add-supplier-btn" className="btn btn-default" onClick={e => this.props.addSupplierToInvite()} data-toggle="tooltip" data-placement="top" title={this.localizeString("Add a supplier row.")}><span className="glyphicon glyphicon-plus" /> {this.localizeString("Supplier")}</button>
                                {" "}
                                <button type="button" className="btn btn-toxnot-danger" onClick={e => this.props.removeSuppliersToInvite()}><span className="glyphicon glyphicon-remove" /> {this.localizeString("Suppliers")}</button>
                                {" "}
                                <button type="button" className="btn btn-default" onClick={e => this.importSuppliersButton_click()}>{this.localizeString("Import Supplier Spreadsheet")}</button>
                                <input type="file" accept=".xlsx, .csv" id="import-suppliers-file-upload" style={{ display: 'none' }} data-file-type="0" onChange={this.importSuppliersFileInput_onChange} />
                                {" "}
                                <a href="/files/Invite%20Suppliers%20Import%20Template.xlsx">{this.localizeString("Download Import Example")}</a>
                            </div>
                            {hasLinkedMaterials && hasMaterialDisclosureLinked && < div className="alert alert-warning">
                                <p>
                                    {this.localizeString("Some of the selected products/materials contain linked materials. 3E Exchange can't ask for Material Disclosure for linked materials. Please edit the template or remove the products/materials with linked materials.")}
                                </p>
                            </div>}
                            <div id="invite-suppliers-core-left-and-main-content">
                                {!this.props.isSelectingSuppliersLoading && this.renderLeftSideSection()}
                                <div id="invite-suppliers-core-main-section">
                                    <SelectSuppliersMaterialsTable
                                        options={{
                                            readOnlyMode: false,
                                            supplierOnlyMode: supplierOnlyMode
                                        }}
                                    />
                                </div>
                            </div>
                            <UserAddNamePromptModal onConfirm={this.userAddNamePrompt_confirmed} />
                        </React.Fragment>
                    );
            }
        }

        return (
            <div id="invite-suppliers-core-full-content" className={this.props.shouldMakeTnSection ? "tn-section" : ""}>
                {fullContent}
                <div id="invite-suppliers-core-scroll-to-div"/>
            </div>
        );
    }

    // ===============================================================================

    private suppliersToInviteHaveInventoriesWithLinkedMaterials() {
        for (const supplierRow of this.props.supplierRowModels) {
            if (supplierRow.supplierId !== null) {
                const supplierInventories = this.props.supplierIdsToInventories[supplierRow.supplierId] ?? [];
                const inventoryIdsWithLinkedMaterials = supplierInventories.filter((i,_) => i.hasLinkedMaterial).map(inv => inv.inventoryId);
                const hasLinkedMaterial = supplierRow.requestedMaterials.some(v => v.inventoryId !== null ? inventoryIdsWithLinkedMaterials.includes(v.inventoryId) : false);
                if (hasLinkedMaterial) {
                    return hasLinkedMaterial;
                }
            }
        }
        return false;
    }

    // ===============================================================================

    private scrollToBottom = () => {
        if ($("#uat-banner")?.length == 0) {
            $("#invite-suppliers-core-scroll-to-div")[0]?.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }

    // ===============================================================================

    private userAddNamePrompt_confirmed = (name: string) => {
        this.props.inviteSuppliers(InviteSupplierStateController.CreateSurveysMode.Send);
    }

    // ===============================================================================

    //
    // UI Events
    //

    private templatePicker_templateSelected = (templateId: string) => {
        this.props.updateCurrentlySelectedTemplate(templateId);

        this.setState({
            openTemplateSelector: false
        });
    }

    // ===============================================================================

    private sendRequestsButton_click = (mode: InviteSupplierStateController.CreateSurveysMode) => {
        const selectedTemplate = this.props.templates.firstOrDefault(t => t.templateId === this.props.currentlySelectedTemplateId);

        if (selectedTemplate === null) {
            throw new Error(`Can't find template: id=${this.props.currentlySelectedTemplateId}`);
        }

        if (selectedTemplate.dataRequested.customQuestionsAsked &&
            (selectedTemplate.bomQuestionnaireId !== null) &&
                (selectedTemplate.dataRequested.materialDisclosureAsked || (selectedTemplate.questionnaireAnswerThatMakesBomDataRequired !== null))) {
            const questionnaire = this.props.questionnaires.firstOrDefault(q => q.questionnaireId === selectedTemplate.bomQuestionnaireId);

            if (questionnaire?.pages.selectMany(page => page.questions).some(question => question.type === "ingredients-table")) {
                alert(this.localizeString("Sorry, your template requests both Material Disclosure and a Custom Questionnaire with an ingredient table. You can't ask for Material Disclosure if you're asking for Ingredient Tables in your Questionnaire."));
                return;
            }
        }
        
        if (this.getUserContextForSignedInUser().fullName.trim() === "") {
            $('#user-add-name-prompt-modal').modal('show');
            return;
        }

        this.props.inviteSuppliers(mode);
    }

    // ===============================================================================

    private inviteSuppliersDone_click = () => {
        this.props.resetState();
        this.props.history.push("/Start");
    }

    // ===============================================================================

    private dataSectionToggle_click = (dataSectionType: InviteSupplierStateController.RequestedDataSectionType) => {
        let newOpenedRequestDataSections = [...this.state.openedRequestDataSections];
        newOpenedRequestDataSections = newOpenedRequestDataSections.filter(r => r !== dataSectionType);

        if (!this.state.openedRequestDataSections.includes(dataSectionType)) {
            newOpenedRequestDataSections.push(dataSectionType)
        }

        this.setState({
            openedRequestDataSections: newOpenedRequestDataSections
        });
    }

    // ===============================================================================

    private importSuppliersButton_click = () => {
        $('#import-suppliers-file-upload').click();
    }

    // ===============================================================================

    private importSuppliersFileInput_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.importSuppliers(e)
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState, ownProps: OwnProps) => ({
        ...state.inviteSuppliersState,
        appServices: state.appServices,
        ...ownProps,
        ...state.selectSuppliersMaterialsTableState
    }),
    { ...InviteSupplierStateController.actionCreators, ...SelectSuppliersMaterialsTableStateController.actionCreators } // Selects which action creators are merged into the component's props
)(withRouter(InviteSuppliersCore) as any);
